import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { AxiosError } from 'axios';
import { authApi } from '@/features/auth/authApi';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = (location.state as any)?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const apiMessage =
        err instanceof AxiosError ? (err.response?.data?.message as string | undefined) : undefined;
      setError(apiMessage ?? 'Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendActivationEmail = async (): Promise<void> => {
    setError('');
    setInfoMessage('');

    if (!email) {
      setError('Email is required to resend activation email.');
      return;
    }

    setIsResending(true);
    try {
      const response = await authApi.resendActivationEmail(email);
      setInfoMessage(
        (response?.message as string | undefined) ?? 'Activation email resent successfully.'
      );
    } catch (err) {
      const apiMessage =
        err instanceof AxiosError ? (err.response?.data?.message as string | undefined) : undefined;
      setError(apiMessage ?? 'Failed to resend activation email.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Login</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {infoMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {infoMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-m text-center text-gray-600">
            Please enter your email and password to login.
          </p>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>

          <Button
            type="button"
            className="w-full bg-gray-600 hover:bg-gray-700 text-white"
            onClick={handleResendActivationEmail}
            disabled={isLoading || isResending}
          >
            {isResending ? 'Resending activation email...' : 'Resend activation email'}
          </Button>
        </form>

        <div className="mt-4 text-sm text-gray-600 text-center space-y-2">
          <p>
            <Link to="/forgot-password" className="text-blue-600">
              Forgot your password?
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};
